import classNames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import { IImage, IVideo } from '../../types';
import { Carousel, CarouselController } from '../Carousel';
import { Image as FiImage } from '../Image/Image';
import { Video as FiVideo } from '../Video/Video';
import styles from './ImageGallery.module.scss';
import * as events from '../../lib/analytics/events';

export interface ImageGalleryProps {
  media: (IImage | IVideo)[];
  grayBackground?: boolean;
  pageDots?: boolean;
  disableAnimation?: boolean;
  parentCarouselControllerRef?: React.MutableRefObject<CarouselController | undefined>;
  squareAspectRatio?: boolean;
  showMobileGallerySelector?: boolean;
  padGallerySelectorImages?: boolean;
  onChange?: (index: number) => void;
  friction?: number;
  wrapAround?: boolean;
  defaultImageIndex?: number;
  noBorderRadius?: boolean;
  /** Flanking prev/next arrows, as the app carousel below the fold shows. */
  prevNextButtons?: boolean;
}

// the gallery frame is capped by its column on desktop and runs full-bleed below that; the
// selector strip is a fixed row of small squares
const SLIDE_SIZES = '(min-width: 1025px) 800px, 100vw';
const THUMB_SIZES = '96px';

const FiMedia = ({
  media,
  priority = false,
  fill = false,
  sizes,
}: {
  media: IImage | IVideo;
  priority?: boolean;
  /** Fills the cell the way the photos do, rather than letterboxing inside it. */
  fill?: boolean;
  /** The slot this renders into, so the browser can size the srcSet against it. */
  sizes?: string;
}) => {
  if (media.type === 'image') {
    return <FiImage image={media} priority={priority} sizes={sizes} />;
  } else {
    return <FiVideo video={media} fill={fill} />;
  }
};

/** Marks a video thumbnail as one: a tinted frame with a play dot, so it reads as playable. */
const VideoThumb = ({ media }: { media: IVideo }) => (
  <div className={styles.videoThumb}>
    <FiMedia media={media.thumbnail} sizes={THUMB_SIZES} />
    <span className={styles.videoThumbDot}>
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M8 5.2v13.6a1 1 0 0 0 1.53.85l10.5-6.8a1 1 0 0 0 0-1.7L9.53 4.35A1 1 0 0 0 8 5.2Z" />
      </svg>
    </span>
  </div>
);

const ImageGallery = ({
  media,
  grayBackground,
  pageDots,
  disableAnimation,
  parentCarouselControllerRef,
  squareAspectRatio,
  showMobileGallerySelector,
  padGallerySelectorImages,
  onChange,
  friction,
  wrapAround,
  defaultImageIndex,
  noBorderRadius,
  prevNextButtons,
}: ImageGalleryProps) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(defaultImageIndex ?? 0);
  const fallbackCarouselControllerRef = useRef<CarouselController>();
  const carouselControllerRef = useMemo(
    () => parentCarouselControllerRef ?? fallbackCarouselControllerRef,
    [parentCarouselControllerRef],
  );

  if (media.length === 0) {
    return null;
  }

  if (media.length === 1) {
    return (
      <div
        className={classNames(styles.featuredImageContainer, {
          [styles.grayBackground]: grayBackground,
          [styles.squareAspectRatio]: squareAspectRatio,
          [styles.noBorderRadius]: noBorderRadius,
        })}
      >
        <FiMedia media={media[0]} priority />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div
        className={classNames(styles.featuredImageContainer, styles.multipleImages, {
          [styles.grayBackground]: grayBackground,
          [styles.squareAspectRatio]: squareAspectRatio,
        })}
      >
        <Carousel
          cellAlign="center"
          className={styles.featuredImageCarousel}
          initialIndex={selectedImageIndex}
          onChange={(index) => {
            setSelectedImageIndex(index);
            onChange && onChange(index);
            events.series3CollarCarouselInteraction(index);
          }}
          pageDots={pageDots}
          controllerRef={carouselControllerRef}
          friction={friction}
          wrapAround={wrapAround}
          prevNextButtons={prevNextButtons}
        >
          {media.map((item, i) => (
            // Only the initial slide is on screen at load; the rest stay lazy.
            <FiMedia media={item} priority={i === (defaultImageIndex ?? 0)} key={i} fill sizes={SLIDE_SIZES} />
          ))}
        </Carousel>
      </div>
      <div
        className={classNames(
          'more-photos',
          styles.morePhotos,
          showMobileGallerySelector && styles.showMobileGallerySelector,
        )}
      >
        {media.map((item, index) => (
          <div
            onClick={() => {
              carouselControllerRef.current?.select(index, disableAnimation);
            }}
            key={index}
            className={classNames(
              styles.galleryPhoto,
              {
                [styles.selected]: selectedImageIndex === index,
              },
              padGallerySelectorImages && styles.padGallerySelectorImages,
            )}
          >
            {item.type === 'image' ? <FiMedia media={item} sizes={THUMB_SIZES} /> : <VideoThumb media={item} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageGallery;
